/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponseBase } from '@angular/common/http';

import { BehaviorSubject, catchError, Observable, Subject, take, throwError } from 'rxjs';

import { environment } from '@env/environment';
import { AccionesConfig as AccionesConfigI, AccionTablaInterface } from './table-actions.interface';
import { actionsUser } from '@constants/actions.constants';

@Injectable({
  providedIn: 'root',
})
export class TableService<T> {
  private _apiAdministracion = environment.apiAdministracion;
  private _apiSecciones = environment.apiSecciones;
  private _apiUsuarios = environment.apiUsuarios;
  private _apiBuscadorCircuitoService = environment.apiBuscadorCircuitoService;
  private _apiPadrones = environment.apiPadrones;
  private _apiPadronesNativos = environment.apiPadronesNativos;

  private readonly _http = inject(HttpClient);

  private createAccion(key: string, event: Function, icon: string, tooltip: string, overrides: Partial<AccionTablaInterface> = {}): AccionTablaInterface {
    return {
      key,
      event,
      icon,
      tooltip,
      isSections: false,
      isBtnVert: false,
      isRedirectToChild: false,
      isVisualizar: false,
      isDelete: false,
      isEdit: false,
      ...overrides,
    };
  }

  getAcciones(config: AccionesConfigI): AccionTablaInterface[] {
    const { sectionsFn, selectFn, redirectToChildFn, visualizarFn, editarFn, eliminarFn, otrasAcciones = [], titleTooltip, iconTooltip } = config;

    const acciones: AccionTablaInterface[] = [];

    if (sectionsFn) {
      acciones.push(this.createAccion('Secciones', sectionsFn, 'repartition', 'Circuito', { isSections: true }));
    }

    if (selectFn) {
      acciones.push(this.createAccion('Secciones', selectFn, 'more_vert', 'Historial', { isBtnVert: true }));
    }

    if (redirectToChildFn) {
      acciones.push(
        this.createAccion(
          titleTooltip || 'Ver',
          (dataParams: any) => redirectToChildFn(titleTooltip, dataParams),
          // 'more_vert',
          iconTooltip || 'more_vert',
          titleTooltip || 'Ver',
          { isRedirectToChild: true },
        ),
      );
    }

    if (visualizarFn) {
      acciones.push(this.createAccion('Visualizar', visualizarFn, 'visibility', 'Visualizar', { isVisualizar: true }));
    }

    if (editarFn) {
      acciones.push(this.createAccion('Editar', editarFn, 'edit', 'Editar', { isEdit: true }));
    }

    if (eliminarFn) {
      acciones.push(this.createAccion('Eliminar', eliminarFn, 'delete', 'Eliminar', { isDelete: true }));
    }

    otrasAcciones.forEach((accion) => {
      acciones.push(
        this.createAccion(accion.key, accion.event, accion.icon, accion.tooltip, {
          isSections: accion.isSections ?? false,
          isBtnVert: accion.isSections ?? false,
          isRedirectToChild: accion.isRedirectToChild ?? false,
          isVisualizar: accion.isVisualizar ?? false,
          isDelete: accion.isDelete ?? false,
          isEdit: accion.isEdit ?? false,
        }),
      );
    });

    return acciones;
  }

  // ========== OBSERVABLES ==========
  // Cargar Paginación
  private paginate: BehaviorSubject<T> = new BehaviorSubject<T>({} as T);
  dataPaginate$: Observable<T> = this.paginate.asObservable();

  public getPagination(): T {
    return this.paginate.getValue();
  }
  public setPagination(data: T): void {
    this.paginate.next(data);
  }

  // ================== Data de búsqueda ==================
  private searchedData: BehaviorSubject<any> = new BehaviorSubject<any>({});
  public searchedData$ = this.searchedData.asObservable();
  public setSearchedData(data: any): void {
    this.searchedData.next(data);
  }

  // Spinner
  private spinner: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public spinner$: Observable<boolean> = this.spinner.asObservable();
  public getSpinner(): boolean {
    return this.spinner.getValue();
  }
  public setSpinner(data: boolean): void {
    this.spinner.next(data);
  }

  private viewSpinner: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public viewSpinner$: Observable<boolean> = this.viewSpinner.asObservable();
  public getViewSpinner(): boolean {
    return this.viewSpinner.getValue();
  }
  public setViewSpinner(data: boolean): void {
    this.viewSpinner.next(data);
  }

  // ================== Verificar si existen datos en la tabla ==================
  private isExistDataTable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public isExistDataTable$: Observable<boolean> = this.isExistDataTable.asObservable();
  public getIsExistDataTable(): boolean {
    return this.isExistDataTable.getValue();
  }
  public setIsExistDataTable(data: boolean): void {
    this.isExistDataTable.next(data);
  }

  // ================== Filtro de búsqueda ==================
  private filtroDeBusquedaAnidada: Subject<T> = new Subject<T>();
  public dataFiltroDeBusqueda$: Observable<T> = this.filtroDeBusquedaAnidada.asObservable();

  public getBusquedaAnidada(): unknown {
    return null;
  }
  public setBusquedaAnidada(data: T): void {
    this.filtroDeBusquedaAnidada.next(data);
  }

  // ================== Filtro de búsqueda Padre  > hijos ==================
  private searchFatherChidren: BehaviorSubject<T> = new BehaviorSubject<T>({} as T);
  public searchFatherChidren$: Observable<T> = this.searchFatherChidren.asObservable();

  public getSearchFatherChidren(): unknown {
    return this.searchFatherChidren.getValue();
  }
  public setSearchFatherChidren(data: T): void {
    this.searchFatherChidren.next(data);
  }

  // ================== Data de la tabla ==================
  private listDataTable = new BehaviorSubject<any[]>([]);
  public listDataTable$ = this.listDataTable.asObservable();
  public setDataTable(data: any[]): void {
    this.listDataTable.next(data);
  }

  public entityName: BehaviorSubject<string> = new BehaviorSubject<string>('');
  entityName$: Observable<string> = this.entityName.asObservable();

  public getEntityName(): any {
    this.entityName.getValue();
  }
  public setEntityName(data: any): void {
    this.entityName.next(data);
  }

  private isIconDelete: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isIconDelete$: Observable<boolean> = this.isIconDelete.asObservable();
  public getIsIconDelete(): boolean {
    return this.isIconDelete.getValue();
  }
  public setIsIconDelete(data: boolean): void {
    this.isIconDelete.next(data);
  }

  private isIconEdit: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isIconEdit$: Observable<boolean> = this.isIconEdit.asObservable();
  public getIsIconEdit(): boolean {
    return this.isIconEdit.getValue();
  }
  public setIsIconEdit(data: boolean): void {
    this.isIconEdit.next(data);
  }

  private isIconView: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isIconView$: Observable<boolean> = this.isIconView.asObservable();
  public getIsIconView(): boolean {
    return this.isIconView.getValue();
  }
  public setIsIconView(data: boolean): void {
    this.isIconView.next(data);
  }

  private isIconVert: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isIconVert$: Observable<boolean> = this.isIconVert.asObservable();
  public getIsIconVert(): boolean {
    return this.isIconVert.getValue();
  }
  public setIsIconVert(data: boolean): void {
    this.isIconVert.next(data);
  }

  private isBtnCreate: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isBtnCreate$: Observable<boolean> = this.isBtnCreate.asObservable();
  public getIsBtnCreate(): boolean {
    return this.isBtnCreate.getValue();
  }
  public setIsBtnCreate(data: boolean): void {
    this.isBtnCreate.next(data);
  }

  // Consultar o listar la data en la tabla
  private isConsulta: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isConsulta$: Observable<boolean> = this.isConsulta.asObservable();
  public getIsConsulta(): boolean {
    return this.isConsulta.getValue();
  }
  public setIsConsulta(data: boolean): void {
    this.isConsulta.next(data);
  }

  // Exportar quiere decir, descargar el reporte en excel
  private isExport: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isExport$: Observable<boolean> = this.isExport.asObservable();
  public getIsExport(): boolean {
    return this.isExport.getValue();
  }
  public setIsExport(data: boolean): void {
    this.isExport.next(data);
  }

  // Modificar o editar la data en la tabla
  private isModificarEditar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isModificarEditar$: Observable<boolean> = this.isModificarEditar.asObservable();
  public getIsModificarEditar(): boolean {
    return this.isModificarEditar.getValue();
  }
  public setIsModificar(data: boolean): void {
    this.isModificarEditar.next(data);
  }

  // Eliminar la data en la tabla
  private isEliminar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isEliminar$: Observable<boolean> = this.isEliminar.asObservable();
  public getIsEliminar(): boolean {
    return this.isEliminar.getValue();
  }
  public setIsEliminar(data: boolean): void {
    this.isEliminar.next(data);
  }

  // Visualizar la data en la tabla
  private isVisualizar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isVisualizar$: Observable<boolean> = this.isVisualizar.asObservable();
  public getIsVisualizar(): boolean {
    return this.isVisualizar.getValue();
  }
  public setIsVisualizar(data: boolean): void {
    this.isVisualizar.next(data);
  }

  // Verificar si se puede ejecutar
  private isEjecutar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isEjecutar$: Observable<boolean> = this.isEjecutar.asObservable();
  public getIsEjecutar(): boolean {
    return this.isEjecutar.getValue();
  }
  public setIsEjecutar(data: boolean): void {
    this.isEjecutar.next(data);
  }

  // Headers adicionales que se envían en la petición
  private isHeaders: BehaviorSubject<any> = new BehaviorSubject<any>({} as any);
  public isHeaders$: Observable<any> = this.isHeaders.asObservable();
  public getIsHeaders(): any {
    return this.isHeaders.getValue();
  }
  public setIsHeaders(data: any): void {
    this.isHeaders.next(data);
  }

  private isVisibleDataTable: BehaviorSubject<T> = new BehaviorSubject<T>({} as T);
  public isVisibleDataTable$: Observable<T> = this.isVisibleDataTable.asObservable();
  public getIsVisibleDataTable(): T {
    return this.isVisibleDataTable.getValue();
  }
  public setIsVisibleDataTable(data: T): void {
    this.isVisibleDataTable.next(data);
  }

  // ========== Verificar y asignar los permisos a las acciones que puede realizar el usuario ==========
  private userPermissions: BehaviorSubject<any> = new BehaviorSubject<any>({});
  public userPermissions$: Observable<any> = this.userPermissions.asObservable();
  public getUserPermissions(): any {
    return this.userPermissions.getValue();
  }
  public setUserPermissions(data: any): void {
    this.userPermissions.next(data);
  }

  // ========== Verificar si se puede visualizar la data en la tabla ==========
  private readonly subject: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
  public readonly observable$: Observable<T[]> = this.subject.asObservable();

  public getValue(): T[] {
    return this.subject.getValue();
  }
  public setValue(data: T[]): void {
    this.subject.next(data);
  }

  // ========== Para descargar el reporte personalizado desde el padre ==========
  private readonly customDownload: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
  public readonly customDownload$: Observable<T[]> = this.customDownload.asObservable();

  public getCustomDownload(): T[] {
    return this.customDownload.getValue();
  }
  public setCustomDownload(data: T[]): void {
    this.customDownload.next(data);
  }

  public downloadReport(body: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.isHeaders$.pipe(take(1)).subscribe((head: any) => {
        const url = this.urlReport(body);
        const headers = new HttpHeaders({
          Accept: 'text/csv',
          module: head?.module || '',
          action: actionsUser.EXPORTAR,
          'origin-resource': head['origin-resource'] || '',
        });

        this._http
          .post(url, body, { headers, responseType: 'text' })
          .pipe(
            catchError((err: HttpResponseBase) => {
              return throwError(() => err);
            }),
          )
          .subscribe({
            next: (response: any) => {
              observer.next(response);
              observer.complete();
            },
            error: (error: any) => {
              observer.error(error);
            },
          });
      });
    });
  }

  private urlReport(body: any): string {
    //console.log(body);
    const entityUrlMap: Record<string, string> = {
      PadronEntity: `${this._apiPadrones}/report`,
      CanalImportacionEntity: `${this._apiPadrones}/report`,
      CampoCanalEntity: `${this._apiPadrones}/report`,
      ClaimEntity: `${this._apiPadrones}/report`,
      ElectorEntity: `${this._apiPadrones}/report`,
      ResidenteEntity: `${this._apiPadrones}/report`,
      ResidenteHistoricoEntity: `${this._apiPadrones}/report`,
      ConsultaRenaperEntity: `${this._apiPadrones}/report`,

      Calle: `${this._apiBuscadorCircuitoService}/report`,
      CalleExcepcion: `${this._apiBuscadorCircuitoService}/report`,
      Barrio: `${this._apiBuscadorCircuitoService}/report`,
      BarrioAlternativo: `${this._apiBuscadorCircuitoService}/report`,
      BarrioAlternativoSinonimo: `${this._apiBuscadorCircuitoService}/report`,
      CodigoPostal: `${this._apiBuscadorCircuitoService}/report`,
      CalleAlternativa: `${this._apiBuscadorCircuitoService}/report`,

      RoleEntity: `${this._apiUsuarios}/report`,
      UserRole: `${this._apiUsuarios}/report`,

      NativePopulationRegistry: `${this._apiPadronesNativos}/report`,
    };

    return entityUrlMap[body.entity] || `${this._apiAdministracion}/report`;
  }
}
